import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  NumberInput,
  NumberInputField,
  Select,
  Text,
  Tooltip,
  useBreakpointValue,
  useToast
} from '@chakra-ui/react'
import { useInvoiceManagement } from '@context'
import { useExperienceManager } from '@hooks'
import { CreditCard } from '@material-ui/icons'
import { formatDollarAmount } from '@utilities'
import { debounce, isInteger } from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isChrome, isDesktop, isMobile } from 'react-device-detect'
import { useQueryClient } from 'react-query'
import {
  getInvoiceControllerFindByIdQueryKey,
  useInvoiceControllerUpdateInvoice,
  useServiceTypeControllerFindAll
} from '~/clinician-api'
import type { InvoiceDto } from '~/clinician-api/models'
import DownloadSuperbillButton from './download-superbill-button'
import InvoiceStatusBadge, { InvoiceStatus } from './invoice-status-badge'
import { LoadingBlock } from './loading-block'

const Invoice = ({
  invoice,
  sessionId,
  noteIsLocked,
  clientNamePrefix,
  isInvoiceLoading
}: {
  invoice: InvoiceDto | undefined
  sessionId: string
  noteIsLocked: boolean
  clientNamePrefix: string
  isInvoiceLoading: boolean
}) => {
  const { isExtension, isWidget } = useExperienceManager()
  const { handleFinalizeSession, isFinalizing, isFinalized } =
    useInvoiceManagement()
  const showText = useBreakpointValue({ base: false, md: true })
  const isSafariOrChrome = isMobile && !isDesktop ? (isChrome ? 107 : 85) : 0
  const notePreferencesBaseHeight = showText ? 205 : 200 + isSafariOrChrome
  const isIntegratedUI = isExtension || isWidget
  const buttonsHaveTextBreakpoint = useBreakpointValue({
    base: false,
    sm: true
  })

  const [parentWidth, setParentWidth] = useState(0)
  const parentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateWidth = () => {
      if (parentRef.current) {
        setParentWidth(parentRef.current.offsetWidth)
      }
    }

    window.addEventListener('resize', updateWidth)
    updateWidth()

    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  const overrideButtonsHaveText = !parentRef?.current || parentWidth > 540
  const buttonsHaveText = buttonsHaveTextBreakpoint && overrideButtonsHaveText

  const chargeButtonDisabled =
    !invoice ||
    invoice.status !== InvoiceStatus.DRAFT ||
    !noteIsLocked ||
    isFinalized

  const [sessionDate, setSessionDate] = useState<string | undefined>()
  const [serviceType, setServiceType] = useState<string | undefined>()
  const [fee, setFee] = useState<number>(0)
  const subtotal = fee || 0
  const amountDue = subtotal

  const isInitialLoadRef = useRef(true)

  const queryClient = useQueryClient()
  const { data: serviceTypeOptions } = useServiceTypeControllerFindAll()
  const toast = useToast()

  const { mutateAsync: updateInvoice } = useInvoiceControllerUpdateInvoice({
    mutation: {
      onSuccess: () => {
        if (invoice?.id) {
          queryClient.invalidateQueries(
            getInvoiceControllerFindByIdQueryKey(invoice.id)
          )
        }
        toast({
          title: 'Invoice updated',
          status: 'success'
        })
      },
      onError: (error: any) => {
        toast({
          title: 'Invoice update failed',
          description:
            error?.message ||
            error?.error?.message ||
            'An error occurred while updating the invoice',
          status: 'error'
        })
      }
    }
  })

  const _debouncedUpdateInvoice = useMemo(
    () =>
      debounce(
        (data: any) => {
          if (!invoice?.id) return
          try {
            updateInvoice({
              invoiceId: invoice.id,
              data
            })
          } catch (error: any) {
            toast({
              title: 'Invoice update failed',
              description: error.message,
              status: 'error'
            })
          }
        },
        1500,
        { trailing: true }
      ),
    [updateInvoice, invoice?.id, toast]
  )

  const _instantUpdateInvoice = useMemo(
    () => (data: any) => {
      if (!invoice?.id) return
      try {
        updateInvoice({
          invoiceId: invoice.id,
          data
        })
      } catch (error) {
        toast({
          title: 'Invoice update failed',
          description: `Please try again.`,
          status: 'error'
        })
      }
    },
    [updateInvoice, invoice?.id, toast]
  )

  const buildUpdateData = (
    newServiceType: string | null,
    newFee: string | null
  ) => {
    if (!invoice?.lineItems?.[0]) {
      toast({
        title: 'Invoice error',
        description: `No line items found.`,
        status: 'error'
      })
      return null
    }

    const currentLineItem = invoice.lineItems[0]
    currentLineItem.sessionId = sessionId

    if (newServiceType !== null) {
      if (newServiceType && serviceTypeOptions) {
        const serviceType = serviceTypeOptions.find(
          (option: any) => option.code === newServiceType
        )

        if (!serviceType) {
          toast({
            title: 'Service type not found',
            description: `Service type ${newServiceType} not found`,
            status: 'error'
          })
          return null
        }
        currentLineItem.serviceTypeCode = serviceType.code

        if (!currentLineItem.price) {
          currentLineItem.price = serviceType.price
        }
      } else {
        // Clear the service type when newServiceType is null
        // @ts-ignore - fix this type
        currentLineItem.serviceTypeCode = null
      }
    }
    if (newFee) {
      if (isInteger(parseInt(newFee)) && parseInt(newFee) >= 0) {
        // @ts-ignore - fix this type
        currentLineItem.priceOverride = parseInt(newFee)
      } else {
        toast({
          title: 'Invalid fee',
          description: `Fee must be an integer greater than or equal to 0`,
          status: 'error'
        })
        return null
      }
    }

    return {
      lineItems: [currentLineItem]
    }
  }

  // show unpaid badge immediately on finalize
  const badgeStatus = useMemo(() => {
    if (invoice?.status === InvoiceStatus.DRAFT && isFinalized) {
      return InvoiceStatus.ISSUED
    }
    return invoice?.status
  }, [invoice?.status, isFinalized])

  useEffect(() => {
    if (invoice) {
      setSessionDate(invoice.lineItems[0].date ?? invoice.date)
      setServiceType(invoice.lineItems[0].serviceTypeCode)
      setFee(invoice.totalGross)
      // Mark initial load as complete
      isInitialLoadRef.current = false
    }
  }, [invoice])

  const handleFeeChange = (valueString: string) => {
    setFee(parseInt(valueString.replace(/^\$/, '')))

    if (!isInitialLoadRef.current && invoice && invoice.id) {
      const updateData = buildUpdateData(null, valueString)
      if (updateData) {
        _debouncedUpdateInvoice(updateData)
      }
    }
  }

  const handleServiceTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newServiceType = e.target.value || undefined
    setServiceType(newServiceType)

    if (!isInitialLoadRef.current && invoice && invoice.id) {
      const updateData = buildUpdateData(newServiceType || null, null)
      if (updateData) {
        _instantUpdateInvoice(updateData)
      }
    }
  }

  if (!isInvoiceLoading && !invoice) {
    return null
  }

  return (
    <>
      <Flex
        height={
          isExtension || isWidget
            ? 'calc(100vh - 165px)'
            : `calc(100vh - ${notePreferencesBaseHeight}px - 24px)`
        }
        gap="16px"
        overflowY="auto"
        mx="32px"
        mt="16px"
      >
        <Flex
          flexDirection="column"
          flex="1"
          gap="16px"
          justifyContent="space-between"
        >
          {isInvoiceLoading ? (
            <LoadingBlock title="Generating invoice..." label="Generating" />
          ) : (
            <Flex flexDirection="column" gap="16px">
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mb="16px"
              >
                <Flex>
                  <Text fontSize="24px" fontWeight="600" color="#333">
                    Invoice
                  </Text>
                  <InvoiceStatusBadge status={badgeStatus} />
                </Flex>
                <Text
                  color="#757575"
                  fontSize="12px"
                  fontWeight="460"
                  lineHeight="73%"
                  letterSpacing="0"
                  textTransform="uppercase"
                  borderRadius="2px"
                  border="1px solid #E4E5E6"
                  p="4px 8px"
                >
                  preview only
                </Text>
              </Flex>

              {/* Date and Session Type Row */}
              <Flex
                display="flex"
                alignItems="flex-start"
                gap="32px"
                alignSelf="stretch"
                flexDirection={{ base: 'column', md: 'row' }}
              >
                {/* Date Section */}
                <Box
                  display="flex"
                  width="163px"
                  flexDirection="column"
                  alignItems="flex-start"
                  gap="12px"
                >
                  <Text fontSize="16px" fontWeight="600" color="#333">
                    Date
                  </Text>
                  <Text fontSize="16px" color="#666">
                    {sessionDate
                      ? new Date(sessionDate).toLocaleDateString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric'
                        })
                      : ''}
                  </Text>
                </Box>

                {/* Session Type Section */}
                <Box
                  display="flex"
                  flexDirection="column"
                  gap="12px"
                  flex="1 0 0"
                >
                  <Text fontSize="16px" fontWeight="600" color="#333">
                    Session type
                  </Text>
                  <Select
                    value={serviceType || ''}
                    onChange={handleServiceTypeChange}
                    borderRadius="4px"
                    disabled={invoice?.status !== 'draft' || isFinalized}
                    borderColor="pale_gray"
                    _focus={{ borderColor: 'primary', outline: 'none' }}
                  >
                    <option value="">Select session type</option>
                    {serviceTypeOptions?.map((option: any) => (
                      <option key={option.code} value={option.code}>
                        {option.code} {option.text}
                      </option>
                    ))}
                  </Select>
                </Box>
              </Flex>

              {/* Edit Fee Section */}
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="16px"
                alignSelf="stretch"
                justifyContent="flex-end"
              >
                <Text fontSize="16px" fontWeight="600" color="#333">
                  Edit fee
                </Text>
                <NumberInput
                  value={fee}
                  onChange={handleFeeChange}
                  min={0}
                  height="40px"
                  max={10000}
                  precision={2}
                  size="sm"
                  width="81px"
                  format={val => formatDollarAmount(val)}
                  parse={val => val.replace(/^\$/, '')}
                  isDisabled={
                    invoice?.status !== InvoiceStatus.DRAFT || isFinalized
                  }
                >
                  <NumberInputField
                    borderRadius="4px"
                    p="8px"
                    height="40px"
                    placeholder="$0.00"
                    disabled={
                      invoice?.status !== InvoiceStatus.DRAFT || isFinalized
                    }
                    _focus={{ borderColor: 'primary', outline: 'none' }}
                  />
                </NumberInput>
              </Box>

              {/* Spacer */}
              <Box h="32px" />

              {/* Divider above totals */}
              <Divider />

              {/* Totals Section */}
              <Flex flexDirection="column" gap="16px" maxW="300px" ml="auto">
                {/* Subtotal */}
                <Flex
                  justifyContent="flex-end"
                  alignItems="flex-start"
                  gap="10px"
                  alignSelf="stretch"
                >
                  <Text
                    fontSize="16px"
                    color="#666"
                    width="200px"
                    textAlign="right"
                  >
                    Subtotal
                  </Text>
                  <Text
                    fontSize="16px"
                    color="#333"
                    fontWeight="500"
                    width="200px"
                    textAlign="right"
                  >
                    ${subtotal.toFixed(2)}
                  </Text>
                </Flex>
              </Flex>

              {/* Divider below subtotal */}
              <Divider />

              {/* Amount Due Section */}
              <Flex flexDirection="column" gap="16px" maxW="300px" ml="auto">
                <Flex
                  justifyContent="flex-end"
                  alignItems="flex-start"
                  gap="10px"
                  alignSelf="stretch"
                >
                  <Text
                    fontSize="18px"
                    color="#333"
                    width="200px"
                    textAlign="right"
                  >
                    Amount due
                  </Text>
                  <Text
                    fontSize="18px"
                    fontWeight="700"
                    color="#333"
                    width="200px"
                    textAlign="right"
                  >
                    ${amountDue.toFixed(2)}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* footer */}
      <Flex
        bg="white"
        borderTop="1px solid"
        borderColor="pale_gray"
        px="16px"
        pb="16px"
      >
        <Flex
          id="invoice-footer"
          w="100%"
          alignItems="center"
          justifyContent="flex-end"
          {...(!isIntegratedUI
            ? { gap: '16px' }
            : { maxWidth: '750px', margin: '0 auto' })}
          ref={parentRef}
          py={isIntegratedUI ? '10px' : '16px'}
        >
          <Flex gap="8px" alignItems="center">
            {/* Superbill Download Button. Only visible for paid invoices */}
            {invoice?.status === InvoiceStatus.BALANCED && (
              <DownloadSuperbillButton
                invoiceId={invoice.id}
                clientNamePrefix={clientNamePrefix}
                buttonsHaveText={buttonsHaveText}
              />
            )}

            {/* Charge Button with conditional tooltip */}
            <Tooltip
              label={
                !noteIsLocked
                  ? 'Please lock your note before sending an invoice'
                  : isFinalized
                    ? 'Invoice has already been sent'
                    : undefined
              }
              placement="bottom-start"
              hasArrow
              shouldWrapChildren
              maxWidth="200px"
              p="6px 8px"
              borderRadius="4px"
              isDisabled={!chargeButtonDisabled}
            >
              <Button
                variant="outline"
                h="35px"
                borderRadius="4px"
                bg="white"
                borderColor="pale_gray"
                m="0"
                {...(!buttonsHaveText
                  ? { w: '35px', minW: '35px', maxW: '35px' }
                  : {})}
                _focus={{ outline: 'none' }}
                onClick={handleFinalizeSession}
                isLoading={isFinalizing}
                disabled={chargeButtonDisabled}
              >
                <CreditCard />
                {buttonsHaveText && (
                  <Text ml="5px">
                    {invoice?.status === InvoiceStatus.DRAFT
                      ? 'Send invoice for session'
                      : 'Invoice sent'}
                  </Text>
                )}
              </Button>
            </Tooltip>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

export default Invoice
